import { AxiosPromise } from "axios"
import { type ButtonTypes } from "components/Atoms/Button"
import { type PhosphorIconProps } from "components/Atoms/PhosphorIcon"
import { emptySubmitChangeFunc } from "./emptySubmitChangeFunc"

interface BaseFormSection<T> {
  type: "collapsable" | "collapsable-start-closed" | "regular"
  key: string
  supportArticleId?: number
  title: string | JSX.Element
  hideTitle?: boolean
  subTitle?: string | JSX.Element
  fields: Array<T>
  hidden?: boolean
}

export type FormSection = BaseFormSection<InPageEditableSectionField | TwoColumnInPageEditableSectionField>
export type EditNewFormSection = BaseFormSection<EditNewSectionField>

export interface MergeFormSection {
  key: string
  hidden?: boolean
  title: string | JSX.Element
  fields: Array<MergeSectionField<any>>
}

export interface AdvancingFormSection<T extends AdvancingSectionField> {
  key: string
  hidden?: boolean
  title: string | JSX.Element
  fields: Array<T>
}

export type CustomSection = {
  type: "custom"
  key: string
  render: () => React.ReactNode
  hidden?: boolean
}

export type Section = FormSection | CustomSection
export type EditNewSection = EditNewFormSection | CustomSection

export interface InPageEditableSectionField {
  key: string
  label: string
  labelTitle?: string
  labelClassName?: string
  valueClassName?: string
  render: () => React.ReactNode
  footer?: React.ReactNode
  sublabel?: string
  hideLabel?: boolean
  compact?: boolean
  inPageEdit?: SectionFieldEdit<any>
  isEmpty?: boolean
  error?: boolean
  ref?: React.RefObject<HTMLDivElement>
  supportArticleId?: number
  hidden?: boolean
  editDisabled?: boolean
}

export type InPageEditableFieldColumn = Omit<InPageEditableSectionField, "label" | "labelClassName" | "valueClassName" | "sublabel" | "hideLabel" | "hidden" | "supportArticleId" | "ref">

export interface TwoColumnInPageEditableSectionField {
  key: string
  label: string
  labelTitle?: string
  labelClassName?: string
  valueClassName?: string
  sublabel?: string
  hideLabel?: boolean
  hidden?: boolean
  isEmpty?: boolean
  ref?: React.RefObject<HTMLDivElement>
  supportArticleId?: number
  left: InPageEditableFieldColumn
  right: InPageEditableFieldColumn
}

export interface MergeSectionField<T extends any> {
  key: string
  label: string
  sublabel?: string
  supportArticleId?: number
  hidden?: boolean
  currentValue: T
  existingValue: T
  sourceValue: T
  render: (value: T) => React.ReactNode
  isEmpty: (value: T) => boolean
  onSourceClick: () => void
  onExistingClick: () => void
}

export interface EditNewSectionField extends Omit<InPageEditableSectionField, "inPageEdit" | "render"> {
  renderEditNew: () => React.ReactNode
  inPageEdit?: NewSectionFieldEdit
}

export interface AdvancingSectionField extends Omit<InPageEditableSectionField, "inPageEdit" | "hideLabel"> {
  phase: server.dto.AdvancingFormItemPhases
  formElementBehavior: server.dto.FormElementBehaviors
  actorName: string
  lastModifiedUtc: Date
}

export interface AdvancingApproveSectionField extends AdvancingSectionField {
  onRemove: () => void
  onValueClick?: () => void
  approveButtons: {
    isVisible: boolean
    isApproving: boolean
    isDeclining: boolean
    onApprove: () => void
    onDecline: () => void
  }
}

export interface AdvancingProvideRenderEditOptions extends RenderEditOptions<any> {
  enter: (value: string) => void
}

export interface AdvancingProvideSectionField extends AdvancingSectionField {
  inPageEdit?: {
    render: (options: AdvancingProvideRenderEditOptions) => React.ReactNode
    initialValue: any
    fullWidth?: boolean
    disableClickOutside?: boolean
  }
  button: {
    type: ButtonTypes
    icon: (props: PhosphorIconProps) => JSX.Element
    isVisible: boolean
    isLoading: boolean
    onClick: () => void
  }
  onEnter: (value: string) => void
}

export type RenderEditOptions<T> = {
  value: T,
  onChange: (value: T, submit?: boolean) => void,
  cancel: () => void
  submit: () => void
  error: boolean
  isSaving: boolean
}

export type SectionFieldEditType = "confirmOrCancelButton" | "confirmOrCancelButtonInline" | "custom"

type SectionFieldEdit<T> = {
  render: (options: RenderEditOptions<T>) => React.ReactNode
  type: SectionFieldEditType
  initialValue: T
  submitChange: (value: T) => AxiosPromise<server.dto.ApiResponse | server.dto.ApiResponseWrapper<any>>
  fullWidth?: boolean
  disableClickOutside?: boolean

  // Saving state is managed by the rendered component, not by the form
  useCustomSavingState?: boolean

  // When true, edit mode activates on hover instead of click. Mouse leave cancels.
  // On devices without hover (mobile), click toggles edit mode.
  hoverEdit?: boolean
  canRemove?: boolean
}

type NewSectionFieldRenderEditOptions = {
  cancel: () => void
  submit: () => void
}

type NewSectionFieldEdit = {
  render: ({ cancel, submit }: NewSectionFieldRenderEditOptions) => React.ReactNode
  fullWidth?: boolean
  disableClickOutside?: boolean
}

export const newSectionFieldEditToSectionFieldEdit = (edit: NewSectionFieldEdit): SectionFieldEdit<any> => ({
  render: ({ cancel, submit }) => edit.render({ cancel, submit }),
  type: "custom",
  initialValue: null,
  submitChange: emptySubmitChangeFunc,
  fullWidth: edit.fullWidth,
  disableClickOutside: edit.disableClickOutside,
})